// ============================================================
// services/VehiclePresetService.js
// EV Powertrain Simulator — 차량 프리셋 CSV 저장/불러오기
//
// ── CSV 구조 ──────────────────────────────────────────────────
//  ## 로 시작하는 줄은 주석 (무시됨)
//  vehicle.id,{고유 키}          ← 차량 구분자 (필수)
//  vehicle.name,{차량 명칭}
//  vehicle.shortName,{약칭}
//  {파라미터 키},{값}
//
//  ★ 여러 차량을 하나의 파일에 연속으로 저장 가능
//    (vehicle.id 행이 나올 때마다 새 차량으로 인식)
//
// ── 카테고리 주석 ─────────────────────────────────────────────
//  ## [CAT] ... 형식의 주석은 파싱 시 무시되며 가독성용
// ============================================================

export const PRESET_VERSION = '1.0';

// ════════════════════════════════════════════════════════════
//  파라미터 카테고리 정의
//  각 카테고리: { label: string, keys: string[] }
// ════════════════════════════════════════════════════════════
export const PARAM_CATEGORIES = [
  {
    id: 'identity',
    label: '차량 식별 / Vehicle Identity',
    comment: '이름 · 색상 · 약칭',
    keys: ['name', 'shortName', 'color'],
  },
  {
    id: 'basic',
    label: '기본 제원 / Basic Specifications',
    comment: '질량 · 공력 · 구름저항 · 타이어',
    keys: [
      'm', 'r_wheel', 'CdA', 'Crr', 'rho', 'mu',
      'wheelbase', 'track_width', 'l_f', 'l_r',
      'I_wheel', 'I_driveshaft', 'rotational_inertia_enable',
      'v_max_chart', 'v_max_sw',
      'altitude', 'rho_air', 'wind_direction', 'wind_speed',
    ],
  },
  {
    id: 'powertrain',
    label: '파워트레인 / Powertrain',
    comment: '기어비 · 최대 토크/출력 · 드라이브라인 효율',
    keys: [
      'motor_config', 'awd_enable',
      'motor_front_power_ratio', 'torque_split_front',
      'gear_ratio', 'eta_driveline',
      'T_max_motor', 'P_max_wheel',
      'P_max_battery', 'P_max_regen',
      'eta_motor_inv', 'aux_power',
      'torque_rate_limit',
      'rear_weight_ratio',
    ],
  },
  {
    id: 'motor',
    label: '모터 (IPMSM) / Motor',
    comment: 'IPMSM 전기 파라미터 · 극수 · 관성',
    keys: [
      'V_dc', 'V_dc_max', 'I_max_rms', 'm_a', 'current_limit',
      'Rs', 'Ld', 'Lq', 'lambda_pm', 'poles', 'J_motor',
      'k_h', 'k_e', 'k_m0', 'k_m1', 'k_m2',
      'steinmetz_kh', 'steinmetz_kc', 'steinmetz_n', 'B_peak_ref', 'V_iron',
      'alpha_Cu', 'k_lambda', 'T_ref',
      'Kp_id', 'Ki_id', 'Kp_iq', 'Ki_iq',
      'Kp_spd', 'Ki_spd',
      'Kp_pos', 'Ki_pos', 'Kd_pos',
      'Id_ref_base', 'mtpa_enable',
      'dead_time', 'ctrl_dt', 'carrier_freq', 'deadtime_us',
      'foc_current_pi_enable',
      'overmod_enable', 'deadtime_compensation_enable', 'dq_convergence_check',
    ],
  },
  {
    id: 'battery',
    label: '배터리 / Battery',
    comment: '팩 용량 · 내부저항 · 열 파라미터 · SOC 관리',
    keys: [
      'battery_energy', 'soc_init', 'pack_mass_kg',
      'R_batt', 'Q_nom', 'batt_temp',
      'k_R_batt',
      'C_th_batt', 'R_th_bc', 'R_th_ba',
      'T_batt_derate_start', 'T_batt_derate_end',
      'T_batt_cold_limit', 'cold_derate_factor',
      // 2RC 모델
      'R1_batt', 'C1_batt', 'R2_batt', 'C2_batt',
      // OCV
      'ocv_a0', 'ocv_a1', 'ocv_a2', 'ocv_a3', 'ocv_a4',
      // SOH
      'soh_profile', 'sei_enable', 'k_SEI_A', 'k_SEI_Ea',
      'k_plating_threshold', 'R_sei_init', 'R_sei_max',
    ],
  },
  {
    id: 'inverter',
    label: '인버터 / Inverter',
    comment: 'SiC MOSFET · 스위칭 손실 · 열 파라미터',
    keys: [
      'V_f_diode', 'R_sw', 'E_on_off', 'f_sw', 'inv_aux_power',
      'C_th_inv', 'R_th_ic', 'k_inv_temp',
      'T_inv_derate_start', 'T_inv_derate_end',
    ],
  },
  {
    id: 'gear',
    label: '기어/효율 / Gear & Efficiency',
    comment: '기어 손실 모델 · 온도 의존성',
    keys: [
      'k_g0', 'k_g1', 'k_g2',
      'gear_T_ref', 'gear_k_viscosity', 'gear_k_bearing', 'gear_k_mesh',
      'gear_T_oil', 'gear_C_th', 'gear_R_th',
      'gear_loss_temp_enable',
    ],
  },
  {
    id: 'thermal',
    label: '열관리 / Thermal Management',
    comment: '냉각수 · 라디에이터 · 모터 열',
    keys: [
      'T_amb',
      'C_th_motor', 'R_th_mc',
      'C_th_cool', 'UA_rad', 'phi_speed',
      'P_pump', 'P_fan',
      'T_motor_derate_start', 'T_motor_derate_end',
      'P_dis_ref', 'P_chg_ref',
      'coolant_flow_opt_enable', 'coolant_flow_base',
      'coolant_flow_min', 'coolant_flow_max',
      'coolant_pump_power_coeff', 'coolant_T_target', 'coolant_T_alarm',
      // HVAC
      'hvac_mode', 'hvac_cop_heat_base', 'hvac_cop_cool_base',
      'hvac_cop_temp_coeff', 'hvac_base_power', 'hvac_max_power', 'hvac_speed_coeff',
    ],
  },
  {
    id: 'awd_tv',
    label: 'AWD · 토크 벡터링 / AWD & Torque Vectoring',
    comment: 'AWD · TV · ESC · TCS',
    keys: [
      'tv_enable', 'Kp_yaw', 'tv_slip_limit',
      'K_p_yaw', 'K_i_yaw', 'tcs_slip_target',
      'esc_enable',
      // 모드별 AWD LUT
      'awd_lut_eco_front', 'awd_lut_normal_front', 'awd_lut_sport_front',
    ],
  },
  {
    id: 'control',
    label: '제어 설정 / Control Settings',
    comment: '발진 제어 · 회생 · 드라이브 모드 · BMS',
    keys: [
      'drive_mode', 'cycle_type', 'cycle_fullspec',
      'one_pedal_enable', 'regen_one_pedal_level',
      'regen_map_enable', 'regen_low_speed_fade_kph', 'regen_max_decel_ms2',
      'regen_blend_start_kph', 'regen_blend_ratio_high',
      'regen_blend_enable', 'regen_blend_k_nonlin',
      'regen_blend_v_lo', 'regen_blend_v_hi',
      'regen_max_jerk', 'friction_brake_max_torque',
      'regen_blend_low_speed', 'eta_regen_motor',
      'regen_optimizer_enable', 'regen_opt_weight_energy', 'regen_opt_weight_comfort',
      'launch_control_enable', 'lc_slip_target',
      'lc_torque_ramp_rate', 'lc_slip_kp', 'lc_slip_ki', 'lc_staging_rpm',
      // BMS
      'bms_balance_current', 'bms_balance_mode', 'bms_fault_latch',
      'bms_balance_threshold', 'bms_cell_v_max', 'bms_cell_v_min',
      'bms_t_hot_cutoff', 'bms_t_cold_derate',
      'bms_i_charge_max', 'bms_i_discharge_max',
      'bms_soc_charge_cutoff', 'bms_soc_discharge_cutoff',
      'bms_t_discharge_min', 'bms_t_discharge_max',
      'bms_cell_count_s', 'bms_cell_count_p',
      'bms_precharge_v', 'bms_regen_soc_taper_start',
      'bms_cold_derate_temp', 'bms_cold_derate_factor',
    ],
  },
  {
    id: 'drivemode_calib',
    label: '드라이브 모드 캘리브레이션 / Drive Mode Calibration',
    comment: 'ECO · NORMAL · SPORT 토크맵 · PI 게인 스케일링',
    keys: [
      'eco_torque_factor', 'eco_regen_factor', 'eco_speed_limit',
      'normal_torque_factor', 'normal_regen_factor',
      'sport_torque_factor', 'sport_regen_factor',
      'eco_kp_scale', 'eco_ki_scale',
      'normal_kp_scale', 'normal_ki_scale',
      'sport_kp_scale', 'sport_ki_scale',
    ],
  },
  {
    id: 'tire',
    label: '타이어 / Tire (Pacejka)',
    comment: 'Pacejka 종/횡 계수 · 온도 모델 · 복합 슬립',
    keys: [
      'tire_pC1', 'tire_pD1', 'tire_pD2', 'tire_pK1', 'tire_pK2', 'tire_pE1', 'tire_pE2',
      'tire_pC1_lat', 'tire_pD1_lat', 'tire_pD2_lat',
      'tire_pK1_lat', 'tire_pK2_lat', 'tire_pE1_lat', 'tire_pE2_lat',
      'tire_thermal_enable', 'tire_temp_coeff_D', 'tire_temp_optimal',
      'combined_slip_enable', 'combined_slip_eps', 'combined_slip_mode',
      'tire_mass', 'tire_cp',
      'tire_lateral_enable',
      'tire_pressure_nominal', 'tire_pressure_actual',
      'tire_pressure_crr_coeff', 'tire_pressure_crr_alpha', 'tire_pressure_crr_beta',
    ],
  },
  {
    id: 'suspension',
    label: '서스펜션 / Suspension',
    comment: 'Quarter-Car · 4WS · 능동 서스펜션',
    keys: [
      'susp_k_f', 'susp_c_f', 'susp_k_r', 'susp_c_r',
      'susp_m_unsprung_f', 'susp_m_unsprung_r',
      'susp_active_enable',
      'steer_4ws_enable', 'steer_4ws_rear_ratio', 'steer_4ws_speed_threshold',
    ],
  },
  {
    id: 'aero',
    label: '공력 / Aerodynamics',
    comment: '그릴 셔터 · 측풍 · 가변 CdA',
    keys: [
      'CdA_cool_open',
      'crosswind_enable', 'crosswind_speed', 'crosswind_angle',
      'grille_shutter_enable', 'grille_shutter_speed_open',
    ],
  },
  {
    id: 'road_env',
    label: '노면/환경 / Road & Environment',
    comment: 'IRI 거칠기 · 고도 · 공기압',
    keys: [
      'IRI', 'k_IRI_crr', 'k_IRI_vib',
      'road_roughness_k', 'road_roughness_enable',
      'Crr_v_coeff', 'Crr_temp_coeff',
      'grade_deg', 'road_condition',
      'grade_profile_enable',
      'rls_mu_enable', 'rls_forgetting', 'rls_P_init', 'mu_min_clip', 'mu_max_clip',
    ],
  },
  {
    id: 'advanced',
    label: '고급/검증 / Advanced & Validation',
    comment: 'Pass/Fail 기준 · Monte Carlo · 민감도',
    keys: [
      'pf_launch100_max', 'pf_launch100_min', 'pf_coast_min',
      'pf_wltp_max_kWh', 'pf_wltp_min_kWh',
      'pf_regen_min_pct', 'pf_eta_total_min',
      'pf_T_motor_max', 'pf_T_batt_max',
      'monte_carlo_runs', 'sensitivity_method', 'mc_seed',
      'kalman_type', 'ekf_alpha', 'ukf_alpha', 'ukf_beta', 'ukf_kappa',
      'mrac_gamma_p', 'mrac_gamma_r',
      'hinf_gamma', 'hinf_w1_bw', 'hinf_w2_bw',
    ],
  },
];

// ════════════════════════════════════════════════════════════
//  직렬화: params 객체 → CSV 라인 배열 (단일 차량)
// ════════════════════════════════════════════════════════════
function serializeVehicle(params, vehicleId) {
  const lines = [];
  const SEP = () => `## ${'─'.repeat(60)}`;
  const C   = (t) => `## ${t}`;

  lines.push(SEP());
  lines.push(C(` 차량 / Vehicle: ${params.name || vehicleId}`));
  lines.push(SEP());

  // vehicle.id 마커
  lines.push(`vehicle.id,${vehicleId || 'custom'}`);
  lines.push('');

  // 카테고리별 출력
  PARAM_CATEGORIES.forEach(cat => {
    const relevant = cat.keys.filter(k => {
      const v = params[k];
      return v !== undefined && v !== null && typeof v !== 'object';
    });
    if (relevant.length === 0) return;

    lines.push(C(` [${cat.label}]  ${cat.comment}`));
    relevant.forEach(k => {
      const v = params[k];
      // 문자열에 콤마 포함 시 따옴표 처리
      const safeV = typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
      lines.push(`${k},${safeV}`);
    });
    lines.push('');
  });

  // 카테고리에 없는 나머지 키도 저장 (누락 방지)
  const allCatKeys = new Set(PARAM_CATEGORIES.flatMap(c => c.keys));
  const extraKeys  = Object.keys(params).filter(k => {
    if (allCatKeys.has(k)) return false;
    if (k === 'vehicle.id') return false;
    const v = params[k];
    return v !== undefined && v !== null && typeof v !== 'object';
  });
  if (extraKeys.length > 0) {
    lines.push(C(' [기타 / Other]'));
    extraKeys.forEach(k => {
      const v = params[k];
      const safeV = typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
      lines.push(`${k},${safeV}`);
    });
    lines.push('');
  }

  return lines;
}

// ════════════════════════════════════════════════════════════
//  역직렬화: CSV 텍스트 → 차량 객체 배열
// ════════════════════════════════════════════════════════════
function castValue(v) {
  if (v === 'true')  return true;
  if (v === 'false') return false;
  if (v.startsWith('"') && v.endsWith('"')) return v.slice(1, -1);
  const n = parseFloat(v);
  return isNaN(n) ? v : n;
}

function parseVehicleCSV(text) {
  const vehicles = [];
  let current    = null;
  let currentId  = null;

  text.split('\n').forEach(rawLine => {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) return; // 주석·빈 줄 스킵

    const ci = line.indexOf(',');
    if (ci === -1) return;
    const key = line.slice(0, ci).trim();
    const val = line.slice(ci + 1).trim();
    if (!key) return;

    if (key === 'vehicle.id') {
      // 새 차량 시작
      if (current !== null) vehicles.push({ id: currentId, params: current });
      currentId = val;
      current   = {};
      return;
    }
    if (current === null) return; // vehicle.id 전에 나온 키는 무시
    if (key === 'ev_sim_vehicle_version') return; // 버전 키 무시
    current[key] = castValue(val);
  });

  if (current !== null) vehicles.push({ id: currentId, params: current });
  return vehicles;
}

// ════════════════════════════════════════════════════════════
//  파일명 생성
// ════════════════════════════════════════════════════════════
function makePresetFilename(label) {
  const d = new Date(), p = n => String(n).padStart(2, '0');
  const safe = (label || 'vehicles').replace(/[^a-zA-Z0-9가-힣_\-]/g, '_').slice(0, 24);
  return `evsim_preset_${safe}_${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}.csv`;
}

function triggerDownload(content, filename) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════
//  Public API
// ════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════
//  템플릿 CSV 파싱
//  A열: 카테고리  B열: 파라미터 키  C열~: 차량별 값
//  ## 로 시작하는 줄 및 빈 줄은 무시
//  name / shortName 행(카테고리 없음)은 특수 처리
// ════════════════════════════════════════════════════════════
function parseTemplateCSV(text) {
  const lines = text.split('\n');

  let vehicleNames   = [];
  let vehicleShorts  = [];
  const paramRows    = [];   // { key, values[] }

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;

    const cells = parseCsvRow(line);
    if (cells.length < 2) continue;

    const col0 = (cells[0] || '').trim();
    const col1 = (cells[1] || '').trim();
    const col2 = (cells[2] || '').trim();

    // ── 포맷 자동 감지 ──────────────────────────────────────
    // v3 포맷: A=카테고리ID, B=파라미터설명(한국어혼재), C=변수명, D~=차량값
    // v2 포맷: A=카테고리ID, B=변수명, C~=차량값
    //
    // 감지 기준:
    //   헤더행(col0=''): col2가 'name'/'shortName'이면 v3
    //   파라미터행:  col2가 순수 변수명 패턴(/^[a-zA-Z_]/) 이면 v3
    //               (v3의 B열은 한국어+영문 혼합 설명, C열이 변수명)

    const isV3Header = col0 === '' && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(col2);
    const isV3Param  = col0 !== '' && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(col2) &&
                       col1 !== col2 &&
                       (/[\uAC00-\uD7A3\u3131-\u318E]/.test(col1) ||   // 한글 포함
                        /\s/.test(col1) ||                               // 공백 포함 (설명구)
                        col1.length > 20);                               // 긴 설명

    const isV3 = isV3Header || isV3Param;

    // name / shortName 헤더 행 (v2: col1='name', v3: col2='name')
    if (col0 === '' && col1.toLowerCase() === 'name') {
      vehicleNames = cells.slice(2).map(v=>(v||'').trim());
      continue;
    }
    if (col0 === '' && col1.toLowerCase() === 'shortname') {
      vehicleShorts = cells.slice(2).map(v=>(v||'').trim());
      continue;
    }
    // v3 헤더: [, 설명, name, 차량1, 차량2, ...]
    if (isV3Header && col2.toLowerCase() === 'name') {
      vehicleNames = cells.slice(3).map(v=>(v||'').trim());
      continue;
    }
    if (isV3Header && col2.toLowerCase() === 'shortname') {
      vehicleShorts = cells.slice(3).map(v=>(v||'').trim());
      continue;
    }

    // 파라미터 행
    if (isV3) {
      // v3: key = col2, values = col3~
      const key  = col2;
      const vals = cells.slice(3).map(v=>(v||'').trim());
      if (key) paramRows.push({ key, values: vals });
    } else {
      // v2 (기존): key = col1, values = col2~
      const key  = col1;
      const vals = cells.slice(2).map(v=>(v||'').trim());
      if (key) paramRows.push({ key, values: vals });
    }
  }

  if (vehicleNames.length === 0) {
    throw new Error('name 행이 없습니다. B열=설명, C열=name(변수명), D열~에 차량 이름을 입력하세요.');
  }

  const count    = vehicleNames.length;
  const vehicles = vehicleNames.map((name, i) => ({
    id: (vehicleShorts[i] || name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || `vehicle_${i+1}`),
    params: {
      name:      name || `Vehicle ${i+1}`,
      shortName: vehicleShorts[i] || '',
    },
  }));

  for (const { key, values } of paramRows) {
    for (let i = 0; i < count; i++) {
      const raw = (values[i] !== undefined && values[i] !== '') ? values[i] : undefined;
      if (raw === undefined) continue;
      vehicles[i].params[key] = castValue(raw);
    }
  }

  return vehicles;
}

/**
 * 단일 CSV 행을 셀 배열로 파싱한다 — 따옴표 내 쉼표를 구분자로 처리하지 않는다.
 * WHY: RFC 4180 전체 구현보다 경량화된 버전으로, 이중따옴표 이스케이프("") 미지원.
 *      완전한 RFC 4180이 필요하면 parseCSVRow(아래)를 사용하라.
 * @param {string} line  CSV 단일 행 문자열
 * @returns {string[]}  파싱된 셀 배열
 */
function parseCsvRow(line) {
  const result = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
    else { cur += ch; }
  }
  result.push(cur);
  return result;
}

// ════════════════════════════════════════════════════════════
//  템플릿 CSV 다운로드 (완성형 — 샘플 3대 포함)
// ════════════════════════════════════════════════════════════
/**
 * 비어있는 차량 프리셋 입력용 템플릿 CSV를 브라우저 다운로드로 내보낸다.
 *
 * PARAM_CATEGORIES에 정의된 모든 파라미터 키를 카테고리별로 나열하고
 * A열: 카테고리 레이블, B열: 파라미터 키, C~열: 차량별 빈 값으로 구성한다.
 * 사용자는 이 파일에 값을 채워 importPresetFromTemplate으로 불러올 수 있다.
 * WHY: 가로형 포맷(차량 = 열)은 여러 차량을 한눈에 비교·편집하기 용이하지만
 *      세로형(차량 = 행)은 Excel에서 필터링하기 쉬우므로 두 포맷을 별도 제공한다.
 *
 * @returns {void}  브라우저 파일 다운로드 트리거 (반환값 없음)
 */
export function downloadPresetTemplate() {
  // 카테고리 → 라벨 맵
  const catLabel = {
    identity:          '차량 식별',
    basic:             '차량 제원',
    powertrain:        '차량 제원',
    motor:             '차량 제원',
    battery:           '차량 제원',
    inverter:          '차량 제원',
    gear:              '차량 제원',
    thermal:           '차량 제원',
    awd_tv:            '구동계 설정',
    control:           '구동계 설정',
    drivemode_calib:   '구동계 설정',
    tire:              '구동계 설정',
    suspension:        '구동계 설정',
    aero:              '차량 제원',
    road_env:          '차량 제원',
    advanced:          '검증/고급',
  };

  const lines = [
    '## EV Sim 차량 프리셋 / Vehicle Preset Template',
    '## A열: 카테고리  B열: 파라미터 키  C열~: 차량별 값',
    '## 차량 추가 시 D열, E열... 오른쪽으로 확장',
    '## ## 로 시작하는 줄은 주석 (파싱 무시)',
    '## name / shortName 행은 특수 행 (카테고리 없음)',
    'name,,차량 A,차량 B,차량 C',
    'shortName,,A,B,C',
  ];

  let lastCat = '';
  for (const cat of PARAM_CATEGORIES) {
    const catLbl = catLabel[cat.id] || cat.id;
    if (catLbl !== lastCat) {
      lines.push(`## ${'─'.repeat(65)}`);
      lines.push(`## [${catLbl}] — ${cat.label}`);
      lines.push(`## ${'─'.repeat(65)}`);
      lastCat = catLbl;
    }
    for (const key of cat.keys) {
      lines.push(`${catLbl},${key},,, `);
    }
  }

  const csv = lines.join('\n');
  triggerDownload(csv, `evsim_preset_template_${new Date().toISOString().slice(0,10)}.csv`);
}

/**
 * 가로형 템플릿 CSV(File 객체)를 파싱하여 차량 배열을 콜백으로 반환한다.
 *
 * v2 포맷(A=카테고리ID, B=변수명, C~=값)과 v3 포맷(A=카테고리ID, B=설명, C=변수명, D~=값)을
 * 자동 감지하여 처리한다. 포맷 판별 기준: C열이 순수 변수명 패턴(/^[a-zA-Z_]/)이면 v3.
 * WHY: 스프레드시트 편집 편의를 위해 B열에 한국어 설명을 추가한 v3를 도입했으나
 *      기존 v2 템플릿과의 하위 호환이 필요하므로 런타임에 포맷을 자동 감지한다.
 *
 * @param {File} file                                               대상 CSV File 객체
 * @param {function({ok:boolean, vehicles:Array, message:string})} onResult  결과 콜백
 * @returns {void}
 */
export function importPresetFromTemplate(file, onResult) {
  if (!file) { onResult?.({ ok: false, vehicles: [], message: '파일이 없습니다.' }); return; }
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const vehicles = parseTemplateCSV(e.target.result);
      if (vehicles.length === 0) {
        onResult?.({ ok: false, vehicles: [], message: 'name 행을 찾을 수 없습니다. 템플릿 형식을 확인하세요.' });
        return;
      }
      onResult?.({ ok: true, vehicles, message: `${vehicles.length}개 차량 불러오기 완료 (${file.name})` });
    } catch (err) {
      onResult?.({ ok: false, vehicles: [], message: `파싱 오류: ${err.message}` });
    }
  };
  reader.onerror = () => onResult?.({ ok: false, vehicles: [], message: '파일 읽기 실패.' });
  reader.readAsText(file, 'utf-8');
}

/**
 * 하나 또는 여러 차량의 파라미터를 세로형 CSV 파일로 내보낸다.
 *
 * 각 차량은 `vehicle.id` 구분자 행으로 시작하며 PARAM_CATEGORIES 순서로 직렬화된다.
 * 카테고리에 없는 파라미터는 '기타/Other' 섹션에 자동 포함된다.
 * 문자열 값에 쉼표가 포함된 경우 따옴표로 감싸 RFC 4180 준수를 보장한다.
 * 여러 차량이 하나의 파일에 연속 저장되므로 importVehiclePresets로 일괄 복원 가능하다.
 *
 * @param {Array<{id:string, params:object}>} vehicles  내보낼 차량 목록
 * @param {string} [label]  파일명 레이블 (미입력 시 차량 ID 조합으로 자동 생성)
 * @returns {string}  생성된 파일 이름
 */
export function exportVehiclePresets(vehicles, label) {
  const lines = [];

  lines.push(`## ${'═'.repeat(60)}`);
  lines.push(`## EV Powertrain Simulator — 차량 프리셋 / Vehicle Presets`);
  lines.push(`## version: ${PRESET_VERSION}   saved: ${new Date().toISOString()}`);
  lines.push(`## 차량 수 / vehicle count: ${vehicles.length}`);
  lines.push(`## ${'═'.repeat(60)}`);
  lines.push('');

  vehicles.forEach(({ id, params }) => {
    serializeVehicle(params, id).forEach(l => lines.push(l));
  });

  const fn = makePresetFilename(label || vehicles.map(v => v.id).join('_'));
  triggerDownload(lines.join('\n'), fn);
  return fn;
}

/**
 * 세로형 차량 프리셋 CSV 파일(File 객체)을 파싱하여 차량 배열을 콜백으로 반환한다.
 *
 * `vehicle.id` 행을 차량 구분자로 사용하며 하나의 파일에 여러 차량이 포함될 수 있다.
 * 각 키-값 행은 castValue로 타입 변환(boolean/number/string)되어 params 오브젝트로 조립된다.
 * vehicle.id 행 이전에 나오는 키는 모두 무시되므로 순서에 유의해야 한다.
 *
 * @param {File} file                                               대상 CSV File 객체
 * @param {function({ok:boolean, vehicles:Array, message:string})} onResult  결과 콜백
 * @returns {void}
 */
export function importVehiclePresets(file, onResult) {
  if (!file) { onResult?.({ ok: false, vehicles: [], message: '파일이 없습니다.' }); return; }
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const vehicles = parseVehicleCSV(e.target.result);
      if (vehicles.length === 0) {
        onResult?.({ ok: false, vehicles: [], message: 'vehicle.id 항목이 없습니다. 올바른 차량 프리셋 CSV인지 확인하세요.' });
        return;
      }
      onResult?.({ ok: true, vehicles, message: `${vehicles.length}개 차량 불러오기 완료 (${file.name})` });
    } catch (err) {
      onResult?.({ ok: false, vehicles: [], message: `파싱 오류: ${err.message}` });
    }
  };
  reader.onerror = () => onResult?.({ ok: false, vehicles: [], message: '파일 읽기 실패.' });
  reader.readAsText(file, 'utf-8');
}

// ════════════════════════════════════════════════════════════
//  가로형 멀티차량 CSV 파서
//  포맷: A열=카테고리, B열=파라미터키, C열~=차량별 값
//  ## 로 시작하는 줄은 주석, 빈 값은 DEFAULT 유지
// ════════════════════════════════════════════════════════════

/**
 * CSV 텍스트 → 셀 배열 (RFC 4180 간이 파서)
 */
function parseCSVRow(line) {
  const cells = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i+1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === ',' && !inQ) {
      cells.push(cur.trim()); cur = '';
    } else {
      cur += c;
    }
  }
  cells.push(cur.trim());
  return cells;
}

/**
 * 가로형 차량 프리셋 CSV 파싱
 * @param {string} text  CSV 전체 텍스트
 * @returns {{ ok:boolean, vehicles:Array<{id:string,params:object}>, message:string, warnings:string[] }}
 */

// ════════════════════════════════════════════════════════════
//  가로형 CSV 포맷 — Public API (aliases + wrapper)
// ════════════════════════════════════════════════════════════

/**
 * 가로형 CSV 텍스트를 파싱하여 차량 배열로 변환한다. (parseTemplateCSV의 public alias)
 *
 * parseTemplateCSV를 try-catch로 감싸 오류 시 { ok:false, ... } 형태의 안전한 결과를 반환한다.
 * 직접 텍스트를 다루는 경우(파일 API 없이 붙여넣기 등)에 활용한다.
 *
 * @param {string} text  CSV 전체 텍스트 (BOM 포함 가능)
 * @returns {{ ok: boolean, vehicles: Array<{id:string, params:object}>, message: string, warnings: string[] }}
 */
export function parseHorizontalPresetCSV(text) {
  try {
    const vehicles = parseTemplateCSV(text);
    return { ok: true, vehicles, message: `${vehicles.length}개 차량 파싱 완료`, warnings: [] };
  } catch (err) {
    return { ok: false, vehicles: [], message: err.message, warnings: [] };
  }
}

/**
 * 가로형 CSV 파일(File 객체)을 읽어 차량 배열을 콜백으로 반환한다.
 *
 * BOM(U+FEFF) 자동 제거 후 parseHorizontalPresetCSV에 위임한다.
 * UTF-8 BOM 포함 Excel 저장 파일을 별도 처리 없이 정상 파싱하기 위함이다.
 *
 * @param {File} file                                                                                  대상 CSV File 객체
 * @param {function({ok:boolean, vehicles:Array, message:string, warnings:string[]})} onResult  결과 콜백
 * @returns {void}
 */
export function importHorizontalPresetCSV(file, onResult) {
  if (!file) { onResult?.({ ok: false, vehicles: [], message: '파일이 없습니다.', warnings: [] }); return; }
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const text = e.target.result.replace(/^\uFEFF/, '');
      const result = parseHorizontalPresetCSV(text);
      onResult?.(result);
    } catch (err) {
      onResult?.({ ok: false, vehicles: [], message: `파싱 오류: ${err.message}`, warnings: [] });
    }
  };
  reader.onerror = () => onResult?.({ ok: false, vehicles: [], message: '파일 읽기 실패.', warnings: [] });
  reader.readAsText(file, 'utf-8');
}

/**
 * 현재 차량 데이터를 v3 포맷 가로형 템플릿 CSV로 내보낸다.
 *
 * v3 포맷 구조:
 *   A열: 카테고리 ID  B열: 파라미터 설명(한국어)  C열: 변수명  D열~: 차량별 값
 * PARAM_CATEGORIES에 없는 파라미터는 'other' 카테고리 행으로 추가된다.
 * 이 함수가 출력하는 v3 포맷은 importHorizontalPresetCSV / importPresetFromTemplate 양쪽에서
 * 모두 파싱 가능하다.
 * WHY: v2에서 B열에 변수명만 있어 한국어 설명이 없었고, v3에서 B열에 설명을 추가하였다.
 *      파서는 B열이 한국어·공백·20자 초과인 경우 v3로 판별하여 C열을 변수명으로 사용한다.
 *
 * @param {Array<{id:string, params:object}>} vehicles  내보낼 차량 목록
 * @param {string} [label]  파일명 레이블
 * @returns {string}  생성된 파일 이름
 */
export function exportHorizontalTemplate(vehicles, label) {
  const names  = vehicles.map(v => v.params.name  || v.id);
  const shorts = vehicles.map(v => v.params.shortName || v.id);

  // ── 파라미터 설명 레이블 맵 (한국어) ──────────────────────────
  const LABELS = {
    name:'차량 이름', shortName:'차량 약칭', color:'차량 색상',
    m:'질량 (Mass)', r_wheel:'바퀴 반경', CdA:'공기저항 (CdA)', Crr:'구름저항 (Crr)',
    rho:'공기 밀도 (ρ)', mu:'노면 마찰계수 (μ)', wheelbase:'축거',
    l_f:'전축→CG 거리 (l_f)', l_r:'후축→CG 거리 (l_r)',
    h_cg:'무게중심 높이 (h_cg)', track_width:'트랙 폭',
    v_max_chart:'최고속도 (그래프)', v_max_sw:'소프트웨어 속도 제한',
    altitude:'고도', rho_air:'공기밀도 (직접 입력)',
    wind_direction:'바람 방향', wind_speed:'바람 속도',
    I_wheel:'바퀴 관성 모멘트', I_driveshaft:'구동축 관성 모멘트',
    rotational_inertia_enable:'회전 관성 활성화',
    motor_config:'모터 구성 (싱글/듀얼)', awd_enable:'AWD 활성화',
    motor_front_power_ratio:'전륜 출력 비율', torque_split_front:'전륜 토크 기본비 (λ)',
    gear_ratio:'감속비 (Gear Ratio)', eta_driveline:'구동계 효율 (η_DL)',
    T_max_motor:'최대 토크 (T_max)', P_max_wheel:'최대 휠 출력 (P_max)',
    P_max_battery:'최대 배터리 전력', P_max_regen:'최대 회생 전력',
    eta_motor_inv:'모터+인버터 효율 (η)', aux_power:'보조부하 전력',
    J_motor:'모터 관성 (J)', torque_rate_limit:'토크 변화율 제한',
    rear_weight_ratio:'후륜 정적 하중 비율', pack_mass_kg:'배터리팩 질량',
    multi_gear_enable:'다단 변속기 활성화',
    gear_shift_speed_kmh:'업시프트 속도', gear_shift_down_speed_kmh:'다운시프트 속도',
    gear_shift_torque_dip:'변속 시 토크 딥', gear_shift_duration_s:'변속 시간',
    battery_energy:'배터리 에너지 (kWh)', soc_init:'초기 SOC',
    R_batt:'배터리 내부 저항 (R)', Q_nom:'정격 용량 (Q)', batt_temp:'배터리 초기 온도',
    V_dc:'DC 링크 전압', V_dc_max:'DC 링크 과전압 한계', I_max_rms:'최대 RMS 전류',
    m_a:'변조 지수 (m_a)',
    bms_cell_count_s:'직렬 셀 수 (S)', bms_cell_count_p:'병렬 셀 수 (P)',
    bms_v_cell_max:'셀 상한 전압', bms_v_cell_min:'셀 하한 전압',
    bms_i_charge_max:'최대 충전 전류', bms_i_discharge_max:'최대 방전 전류',
    bms_cold_derate_temp:'저온 디레이팅 온도', bms_cold_derate_factor:'저온 디레이팅 계수',
    bms_soc_charge_cutoff:'충전 차단 SOC', bms_soc_discharge_cutoff:'방전 차단 SOC',
    bms_regen_soc_taper_start:'회생 SOC 테이퍼 시작',
    bms_t_charge_min:'충전 최저 온도', bms_t_charge_max:'충전 최고 온도',
    bms_t_discharge_min:'방전 최저 온도', bms_t_discharge_max:'방전 최고 온도',
    bms_t_hot_cutoff:'고온 충전 차단 온도', bms_precharge_v:'프리차지 목표 전압',
    bms_fault_latch:'BMS 결함 래치', bms_detailed_enable:'셀 상세 시뮬 활성화',
    battery_model:'배터리 모델 (simple/2RC)',
    k_R_batt:'저항 온도계수 (k_R)', C_th_batt:'배터리 열용량',
    R_th_bc:'배터리-냉각수 열저항', R_th_ba:'배터리-외기 열저항',
    T_batt_derate_start:'배터리 디레이팅 시작 온도', T_batt_derate_end:'배터리 디레이팅 종료 온도',
    T_batt_cold_limit:'배터리 저온 제한 온도', cold_derate_factor:'저온 출력 계수',
    Rs:'고정자 저항 (Rs)', Ld:'d축 인덕턴스 (Ld)', Lq:'q축 인덕턴스 (Lq)',
    lambda_pm:'자속쇄수 (λ_pm)', poles:'극수',
    k_h:'철손 히스테리시스 계수', k_e:'철손 와전류 계수',
    k_m0:'기계손 정수 (k_m0)', k_m1:'기계손 1차 계수', k_m2:'기계손 2차 계수',
    alpha_Cu:'구리 온도계수 (α)', k_lambda:'자속 온도계수', T_ref:'기준 온도',
    Kp_id:'전류 PI d축 Kp', Ki_id:'전류 PI d축 Ki',
    Kp_iq:'전류 PI q축 Kp', Ki_iq:'전류 PI q축 Ki',
    Kp_spd:'속도 PI Kp', Ki_spd:'속도 PI Ki',
    Kp_pos:'위치 제어 Kp', Ki_pos:'위치 제어 Ki', Kd_pos:'위치 제어 Kd',
    Id_ref_base:'d축 전류 기준값', mtpa_enable:'MTPA 활성화',
    fw_enable:'약계자 활성화', fw_v_margin:'FW 전압 마진',
    dead_time:'데드타임', deadtime_us:'데드타임 (μs)',
    carrier_freq:'캐리어 주파수', ctrl_dt:'제어 주기',
    f_sw:'스위칭 주파수', foc_current_pi_enable:'전류 PI 시뮬 연동',
    overmod_enable:'과변조 활성화', deadtime_compensation_enable:'데드타임 보상 활성화',
    V_f_diode:'다이오드 순방향 전압 (V_f)', R_sw:'스위치 온저항 (R_sw)',
    E_on_off:'스위칭 에너지 (E_on_off)', inv_aux_power:'인버터 보조 전력',
    k_inv_temp:'인버터 손실 온도계수', C_th_inv:'인버터 열용량',
    R_th_ic:'인버터 냉각 열저항',
    T_inv_derate_start:'인버터 디레이팅 시작 온도', T_inv_derate_end:'인버터 디레이팅 종료 온도',
    k_g0:'기어 정적 손실 (k_g0)', k_g1:'기어 토크비례 손실 (k_g1)',
    k_g2:'기어 속도비례 손실 (k_g2)',
    gear_T_oil:'기어오일 초기 온도', gear_loss_temp_enable:'기어 온도의존 손실 활성화',
    eta_regen_motor:'회생 모터 효율',
    P_chg_ref:'최대 충전 기준 전력', P_dis_ref:'최대 방전 기준 전력',
    regen_blend_low_speed:'회생 저속 블렌딩 속도',
    T_amb:'주변 온도 (T_amb)', C_th_motor:'모터 열용량',
    R_th_mc:'모터 냉각 열저항', C_th_cool:'냉각수 열용량',
    UA_rad:'라디에이터 열전달 계수 (UA)', phi_speed:'냉각 속도 계수 (φ)',
    P_pump:'냉각 펌프 전력', P_fan:'냉각 팬 전력',
    T_motor_derate_start:'모터 디레이팅 시작 온도', T_motor_derate_end:'모터 디레이팅 종료 온도',
    tv_enable:'토크 벡터링 활성화', Kp_yaw:'TV 요레이트 게인',
    tv_slip_limit:'슬립 한계', lambda_fr_base:'전후 기본 배분 λ0',
    lambda_fr_min:'전후 최소 배분', lambda_fr_max:'전후 최대 배분',
    tcs_enable:'TCS 활성화', tcs_slip_target:'TCS 목표 슬립률',
    esc_enable:'ESC 활성화', abs_enable:'ABS 활성화',
    abs_lambda_target:'ABS 목표 슬립률', abs_Kp:'ABS 비례 게인',
    drive_mode:'드라이브 모드', cycle_type:'주행 사이클 타입',
    one_pedal_enable:'원페달 드라이빙 활성화', regen_one_pedal_level:'원페달 회생 강도',
    regen_max_decel_ms2:'최대 회생 감속도', regen_low_speed_fade_kph:'회생 저속 페이드 속도',
    regen_blend_start_kph:'회생 블렌딩 시작 속도', regen_blend_ratio_high:'고속 회생 블렌딩 비율',
    regen_blend_enable:'비선형 회생 블렌딩 활성화', friction_brake_max_torque:'최대 마찰 제동 토크',
    launch_control_enable:'런치 컨트롤 활성화', lc_slip_target:'런치 컨트롤 목표 슬립률',
    lc_torque_ramp_rate:'런치 컨트롤 토크 램프율', lc_slip_kp:'런치 컨트롤 슬립 Kp',
    lc_slip_ki:'런치 컨트롤 슬립 Ki', lc_staging_rpm:'런치 컨트롤 스테이징 RPM',
    eco_pedal_sensitivity:'ECO 페달 감도', eco_max_torque_ratio:'ECO 최대 토크 비율',
    eco_regen_strength:'ECO 회생 강도',
    normal_pedal_sensitivity:'NORMAL 페달 감도', normal_max_torque_ratio:'NORMAL 최대 토크 비율',
    normal_regen_strength:'NORMAL 회생 강도',
    sport_pedal_sensitivity:'SPORT 페달 감도', sport_max_torque_ratio:'SPORT 최대 토크 비율',
    sport_regen_strength:'SPORT 회생 강도',
    snow_pedal_sensitivity:'SNOW 페달 감도', snow_max_torque_ratio:'SNOW 최대 토크 비율',
    snow_regen_strength:'SNOW 회생 강도',
    grade_deg:'도로 경사 (grade_deg)', road_condition:'노면 상태',
    IRI:'도로 거칠기 지수 (IRI)', Crr_v_coeff:'Crr 속도계수', Crr_temp_coeff:'Crr 온도계수',
    sei_enable:'SEI 성장 모델 활성화', soh_enable:'SoH 추적 활성화',
    li_plating_enable:'리튬 플레이팅 감지 활성화', kalman_enable:'칼만 필터 활성화',
    pf_launch100_max:'발진 100km/h 최대 시간', pf_launch100_min:'발진 100km/h 최소 시간',
    pf_wltp_max_kWh:'WLTP 소비 전력 상한', pf_wltp_min_kWh:'WLTP 소비 전력 하한',
    pf_T_motor_max:'모터 온도 최대 기준', pf_T_batt_max:'배터리 온도 최대 기준',
    monte_carlo_runs:'몬테카를로 반복 횟수',
  };
  const getLabel = k => LABELS[k] || k;

  // ── CSV 값 안전 포맷 ──────────────────────────────────────────
  const safeVal = v => {
    if (v === undefined || v === null) return '';
    const s = String(v);
    return (s.includes(',') || s.includes('"')) ? `"${s.replace(/"/g,'""')}"` : s;
  };

  // ── 행 빌드 ───────────────────────────────────────────────────
  const rows = [];
  rows.push('## ══════════════════════════════════════════════════════');
  rows.push('## EV Powertrain Simulator — 차량 프리셋 템플릿');
  rows.push('## ══════════════════════════════════════════════════════');
  rows.push(`## 차량 ${vehicles.length}종  저장일시: ${new Date().toISOString()}`);
  rows.push('## A열: 카테고리ID  B열: 파라미터 설명(한국어)  C열: 변수명  D열~: 차량별 값');
  rows.push('## 규칙: ##주석 무시 | 빈값=DEFAULT 사용 | 차량 추가=열 추가');
  rows.push('## ══════════════════════════════════════════════════════');
  rows.push('');

  // 헤더 행
  rows.push(`,차량 이름,name,${names.map(n => safeVal(n)).join(',')}`);
  rows.push(`,차량 약칭,shortName,${shorts.map(s => safeVal(s)).join(',')}`);
  rows.push('');

  // 카테고리별 파라미터
  PARAM_CATEGORIES.forEach(cat => {
    const relevant = cat.keys.filter(k =>
      vehicles.some(v => {
        const val = v.params[k];
        return val !== undefined && val !== null && typeof val !== 'object';
      })
    );
    if (!relevant.length) return;

    rows.push(`## ── ${cat.label}  (${cat.comment})`);
    relevant.forEach(k => {
      const vals = vehicles.map(v => {
        const val = v.params[k];
        if (val === undefined || val === null) return '';
        return safeVal(val);
      });
      // A=카테고리ID, B=설명(한국어), C=변수명, D~=차량별값
      rows.push(`${cat.id},${safeVal(getLabel(k))},${k},${vals.join(',')}`);
    });
    rows.push('');
  });

  // PARAM_CATEGORIES 미포함 파라미터 (other)
  const allCatKeys = new Set(PARAM_CATEGORIES.flatMap(c => c.keys));
  const otherKeys = vehicles
    .flatMap(v => Object.keys(v.params))
    .filter(k => !allCatKeys.has(k) && !['name','shortName','color'].includes(k));
  const uniqueOther = [...new Set(otherKeys)].filter(k =>
    vehicles.some(v => v.params[k] !== undefined && v.params[k] !== null && typeof v.params[k] !== 'object')
  );
  if (uniqueOther.length) {
    rows.push('## ── 기타 / Other');
    uniqueOther.forEach(k => {
      const vals = vehicles.map(v => safeVal(v.params[k] ?? ''));
      rows.push(`other,${safeVal(getLabel(k))},${k},${vals.join(',')}`);
    });
    rows.push('');
  }

  const fn = makePresetFilename(label || 'preset_template');
  triggerDownload(rows.join('\n'), fn);
  return fn;
}